interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
}

/**
 * 표시할 페이지 번호 목록. 페이지가 많아도 최대 5개 번호 + 생략(…)만 렌더해
 * 모바일 폭을 넘기지 않도록 한다.
 */
function buildPageItems(currentPage: number, totalPages: number): (number | 'gap')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // 현재 페이지 주변 3개를 유지하되, 양 끝에서는 안쪽으로 밀어 폭을 일정하게 둔다.
  const middleStart = Math.max(2, Math.min(currentPage - 1, totalPages - 3));
  const middleEnd = Math.min(totalPages - 1, Math.max(currentPage + 1, 4));

  const items: (number | 'gap')[] = [1];
  if (middleStart > 2) items.push('gap');
  for (let p = middleStart; p <= middleEnd; p += 1) items.push(p);
  if (middleEnd < totalPages - 1) items.push('gap');
  items.push(totalPages);

  return items;
}

export default function Pagination({ currentPage, totalPages, onChange }: PaginationProps) {
  if (totalPages <= 1) return null;

  const items = buildPageItems(currentPage, totalPages);

  return (
    <nav
      aria-label="Pagination"
      className="mt-6 flex flex-wrap items-center justify-center gap-1 text-sm"
    >
      <button
        type="button"
        onClick={() => onChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="이전 페이지"
        className="rounded px-2 py-1 hover:bg-slate-100 disabled:cursor-default disabled:opacity-30"
      >
        ←
      </button>
      {items.map((item, i) =>
        item === 'gap' ? (
          <span
            key={`gap-${i}`}
            aria-hidden="true"
            className="px-1 py-1 text-slate-400 select-none"
          >
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onChange(item)}
            aria-current={item === currentPage ? 'page' : undefined}
            className={
              item === currentPage
                ? 'min-w-8 rounded bg-slate-900 px-2 py-1 font-bold text-white'
                : 'min-w-8 rounded px-2 py-1 hover:bg-slate-100'
            }
          >
            {item}
          </button>
        ),
      )}
      <button
        type="button"
        onClick={() => onChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="다음 페이지"
        className="rounded px-2 py-1 hover:bg-slate-100 disabled:cursor-default disabled:opacity-30"
      >
        →
      </button>
    </nav>
  );
}
